import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Download, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './ui/table';
import { fmtINR, fmtCompact } from '../lib/utils';
import { toast } from 'sonner';

// --- Constants extracted outside components ---

const sectionAnim = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const CHART_MARGIN = { top: 10, right: 16, left: 0, bottom: 0 };
const AXIS_TICK_STYLE = { fill: 'hsl(220 9% 46%)', fontSize: 12 };
const AXIS_TICK_STYLE_SM = { fill: 'hsl(220 9% 46%)', fontSize: 11 };
const X_AXIS_LABEL = { value: 'Year', position: 'insideBottom', offset: -5, style: { fill: 'hsl(220 9% 46%)', fontSize: 11 } };
const GRID_COLOR = 'hsl(220 16% 90%)';
const BALANCE_COLOR = 'hsl(226 58% 22%)';
const BALANCE_FILL = 'hsl(226 58% 22% / 0.12)';
const WITHDRAWAL_COLOR = 'hsl(43 74% 52%)';
const REF_LINE_COLOR = 'hsl(160 45% 35%)';
const MAX_CHART_POINTS = 50;

// --- Data transformation helpers ---

function buildChartData(result) {
  if (!result?.schedule) return [];
  return [
    { year: 0, closing_balance: result.initial_corpus, withdrawal: 0, growth: 0 },
    ...result.schedule.map((row) => ({
      year: row.year,
      closing_balance: row.closing_balance,
      withdrawal: row.withdrawal,
      growth: row.growth,
      opening_balance: row.opening_balance,
    })),
  ];
}

function sampleDataForDisplay(data) {
  if (data.length <= MAX_CHART_POINTS) return data;
  const step = Math.ceil(data.length / MAX_CHART_POINTS);
  return data.filter((_, i) => i === 0 || i === data.length - 1 || i % step === 0);
}

// --- Sub-components ---

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const dataMap = {};
  payload.forEach((p) => {
    dataMap[p.dataKey] = p.value;
  });
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-md" data-testid="chart-tooltip">
      <div className="text-xs font-medium text-muted-foreground mb-1.5">Year {label}</div>
      <div className="space-y-1">
        {dataMap.closing_balance !== undefined && (
          <div className="text-sm font-semibold text-primary">Balance: {fmtINR(dataMap.closing_balance)}</div>
        )}
        {dataMap.withdrawal !== undefined && (
          <div className="text-xs text-muted-foreground">Withdrawal: {fmtINR(dataMap.withdrawal)}</div>
        )}
        {dataMap.growth !== undefined && (
          <div className="text-xs text-muted-foreground">Growth: {fmtINR(dataMap.growth)}</div>
        )}
      </div>
    </div>
  );
};

const yAxisFormatter = (v) => `\u20B9${fmtCompact(v)}`;

const CorpusChart = ({ displayData, initialCorpus, targetMultiplier }) => {
  const mult = targetMultiplier || 2;
  const multLabel = mult === Math.floor(mult) ? `${Math.floor(mult)}x Target` : `${mult}x Target`;
  const refLabelStyle = useMemo(() => ({ value: multLabel, position: 'right', style: { fill: REF_LINE_COLOR, fontSize: 10 } }), [multLabel]);

  return (
    <div data-testid="visualization-corpus-trend-chart">
      <ResponsiveContainer width="100%" height={340}>
        <AreaChart data={displayData} margin={CHART_MARGIN}>
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="4 4" />
          <XAxis dataKey="year" tick={AXIS_TICK_STYLE} label={X_AXIS_LABEL} />
          <YAxis tickFormatter={yAxisFormatter} tick={AXIS_TICK_STYLE_SM} width={70} />
          <Tooltip content={<CustomTooltip />} />
          {initialCorpus > 0 && mult > 0 && (
            <ReferenceLine y={initialCorpus * mult} stroke={REF_LINE_COLOR} strokeDasharray="6 4" label={refLabelStyle} />
          )}
          <Area type="monotone" dataKey="closing_balance" stroke={BALANCE_COLOR} fill={BALANCE_FILL} strokeWidth={2} isAnimationActive animationDuration={400} />
          <Line type="monotone" dataKey="withdrawal" stroke={WITHDRAWAL_COLOR} strokeWidth={2} dot={false} isAnimationActive animationDuration={400} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CashflowTable = ({ schedule }) => (
  <div className="max-h-[400px] overflow-auto rounded-lg border border-border" data-testid="visualization-yearly-cashflow-table">
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card">
        <TableRow>
          <TableHead className="text-center w-16">Year</TableHead>
          <TableHead className="text-right">Opening Balance</TableHead>
          <TableHead className="text-right">Growth</TableHead>
          <TableHead className="text-right">Withdrawal (Monthly)</TableHead>
          <TableHead className="text-right">Withdrawal (Yearly)</TableHead>
          <TableHead className="text-right">Closing Balance</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {schedule.map((row) => (
          <TableRow key={row.year} className={row.closing_balance <= 0 ? 'bg-red-50' : ''}>
            <TableCell className="text-center font-mono-num text-xs">{row.year}</TableCell>
            <TableCell className="text-right font-mono-num text-xs">{fmtINR(row.opening_balance)}</TableCell>
            <TableCell className="text-right font-mono-num text-xs text-green-700">+{fmtINR(row.growth)}</TableCell>
            <TableCell className="text-right font-mono-num text-xs text-red-600">₹{fmtINR(row.withdrawal / 12)}</TableCell>
            <TableCell className="text-right font-mono-num text-xs text-red-600">₹{fmtINR(row.withdrawal)}</TableCell>
            <TableCell className="text-right font-mono-num text-xs font-semibold">{fmtINR(row.closing_balance)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

// --- Main component ---

export const CorpusVisualization = ({ result, onDownloadCsv }) => {
  const chartData = useMemo(() => buildChartData(result), [result]);
  const displayData = useMemo(() => sampleDataForDisplay(chartData), [chartData]);

  const handleCsvDownload = useCallback(async () => {
    toast.loading('Preparing CSV...');
    const success = await onDownloadCsv();
    toast.dismiss();
    if (success) {
      toast.success('CSV downloaded successfully!');
    } else {
      toast.error('Failed to download CSV. Please try again.');
    }
  }, [onDownloadCsv]);

  if (!result) return null;

  return (
    <section id="visuals" className="py-12 sm:py-16 lg:py-20 bg-secondary/30" data-testid="visualization-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...sectionAnim} className="text-center mb-10">
          <Badge variant="outline" className="mb-3 text-xs font-medium">
            <BarChart3 size={12} className="mr-1" />
            Visual Analysis
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">Corpus Trend & Cashflow</h2>
          <p className="mt-2 text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Visualize how your wealth evolves year by year with inflation-adjusted withdrawals.
          </p>
        </motion.div>

        <motion.div {...sectionAnim}>
          <Card className="border border-border shadow-md mb-8">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-primary">Corpus Balance Over Time</h3>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-[hsl(226,58%,22%)] rounded" /> Balance
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-[hsl(43,74%,52%)] rounded" /> Withdrawal
                  </span>
                </div>
              </div>
              <CorpusChart displayData={displayData} initialCorpus={result.initial_corpus} targetMultiplier={result.target_multiplier} />
            </CardContent>
          </Card>
        </motion.div>

        <motion.div {...sectionAnim}>
          <Card className="border border-border shadow-md">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-primary">Year-by-Year Cashflow</h3>
                <Button variant="outline" size="sm" className="rounded-xl text-xs h-8" onClick={handleCsvDownload} data-testid="download-csv-button">
                  <Download size={14} className="mr-1" /> Export CSV
                </Button>
              </div>
              <CashflowTable schedule={result.schedule} />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default CorpusVisualization;
